import { z } from 'zod'
import type { SpaceKind } from './operations'
import type { SourceRoom, SourceRoomLayout } from './source-rooms'
import type { Sourced } from './sources'

// Booking's venues and rooms in Hub as facilities and spaces (ADR 0025).
//
// - A Booking venue is mapped to a Hub facility once, by a person
//   (facilities.manage). Hub may suggest the facility with the same name,
//   but a suggestion does nothing until someone confirms it.
// - Each active room of a mapped venue becomes a Hub space under that
//   facility, linked to the room (space_source_link, as a hand-made link
//   is), with Booking's capacities as its layouts. A room already linked to
//   a Hub space by hand keeps that space.
// - Booking stays authoritative for the spaces it made: their name, status
//   and Booking layouts change only in Booking. A room that goes inactive or
//   away makes its Hub space inactive and flags it for review; nothing is
//   deleted.
//
// Read from Booking's room feed (source-rooms.ts), which has no venue ids:
// a room names its venue, and Booking groups rooms by that name. The name is
// the venue's key.

export const SPACE_IMPORT_SYSTEM_SUB = 'system:space-import'
export const SPACE_IMPORT_SOURCE = 'booking'

export type SpaceImportTrigger = 'scheduled' | 'refresh'

// The layouts the import keeps equal to a room's capacities in Booking.
export const BOOKING_CAPACITY_LAYOUTS = [
  { key: 'booking:seated', name: 'Seated', field: 'capacitySeated' },
  { key: 'booking:standing', name: 'Standing', field: 'capacityStanding' },
] as const

// Where a space came from, for labels: made by the import from a Booking
// room, made in Hub and linked to a room by hand, or Hub-only.
export type SpaceOrigin = 'booking' | 'linked' | 'hub'

// What happens (or happened) to one Booking room.
//   imported        the import made its Hub space; kept current from Booking
//   create          its venue is mapped and it has no Hub space: the next
//                   run makes one
//   linked_by_hand  staff linked it to a Hub space; that space stays as it is
//   wait_venue      its venue isn't mapped to a facility yet
//   name_taken      the mapped facility already has a Hub space by that name;
//                   link that space to the room by hand, or rename it
//   inactive        inactive in Booking and never imported: not brought over
export const ROOM_PLAN_ACTIONS = ['imported', 'create', 'linked_by_hand', 'wait_venue', 'name_taken', 'inactive'] as const
export type RoomPlanAction = (typeof ROOM_PLAN_ACTIONS)[number]

export type RoomFlag = 'deactivated' | 'deleted'

export type RoomPlan = {
  roomId: string
  name: string
  venue: string
  capacitySeated: number | null
  capacityStanding: number | null
  active: boolean
  bookable: boolean
  layouts: SourceRoomLayout[]
  url: string | null
  action: RoomPlanAction
  facility: { id: string; name: string } | null
  // The Hub space it is, or (name_taken) the one in the way.
  space: { id: string; name: string; status: 'active' | 'inactive' } | null
  flag: RoomFlag | null
}

export type VenueMapping = {
  linkId: string
  facilityId: string
  facilityName: string
  method: 'picked' | 'suggestion' | 'created'
  linkedAt: string
  linkedByName: string | null
}

export type BookingVenue = {
  // Booking's venue name: the key the mapping stores.
  key: string
  mapping: VenueMapping | null
  // A facility whose name is the same once case, accents, punctuation and a
  // leading "The" are ignored. Only shown; never applied without a click.
  suggestion: { facilityId: string; facilityName: string } | null
  rooms: RoomPlan[]
}

// A room Hub has a space for that Booking no longer lists.
export type GoneRoom = { roomId: string; name: string; venue: string; space: { id: string; name: string; status: 'active' | 'inactive' }; origin: 'booking' | 'linked'; goneAt: string | null }

export type SpaceImportPreview = { venues: BookingVenue[]; gone: GoneRoom[]; mappingsWithoutVenue: (VenueMapping & { key: string })[] }

// GET /api/booking/venues: Booking's venues read live, with what Hub has.
export type BookingVenuesView = {
  rooms: Sourced<{ asOf: string; count: number }>
  preview: SpaceImportPreview | null
  importOn: boolean
}

export type SpaceImportCounts = {
  rooms: number
  created: number
  updated: number
  labelsRefreshed: number
  waitingVenue: number
  nameTaken: number
  inactiveSkipped: number
  deactivated: number
  reactivated: number
  gone: number
}
export const emptySpaceImportCounts = (): SpaceImportCounts => ({ rooms: 0, created: 0, updated: 0, labelsRefreshed: 0, waitingVenue: 0, nameTaken: 0, inactiveSkipped: 0, deactivated: 0, reactivated: 0, gone: 0 })

export type SpaceImportRun = {
  id: string
  trigger: SpaceImportTrigger
  startedAt: string
  finishedAt: string | null
  status: 'running' | 'ok' | 'error' | 'not_connected'
  counts: SpaceImportCounts
  error: string | null
  sourceAsOf: string | null
  startedBy: { personId: string; displayName: string } | null
}

export type SpaceImportStatus = {
  lastRun: SpaceImportRun | null
  lastSuccess: SpaceImportRun | null
  running: boolean
  mappedVenues: number
  importedSpaces: number
  linkedByHand: number
  toReview: number
}

export type SpaceImportResult =
  | { status: 'off'; message: string }
  | { status: 'not_connected' | 'unavailable' | 'busy' | 'error'; message: string }
  | { status: 'ran'; run: SpaceImportRun }

// A space's Booking source, when the import made it.
export type SpaceBookingImport = {
  roomId: string
  venue: string
  // What Booking last called it (differs from the Hub name only when a
  // rename in Booking couldn't be applied).
  bookingName: string
  url: string | null
  active: boolean
  layouts: SourceRoomLayout[]
  lastSeenAt: string
  flag: RoomFlag | null
  flaggedAt: string | null
  reviewStatus: 'open' | 'reviewed' | null
}

// A space whose room is flagged, for the hand-linked case too.
export type SpaceRoomFlag = { flag: RoomFlag; flaggedAt: string | null; reviewStatus: 'open' | 'reviewed' | null }

// ------------------------------------------------------------- bodies

export const VenueMapBody = z.object({
  venue: z.string().trim().min(1).max(200),
  facilityId: z.string().min(1).max(100),
  method: z.enum(['picked', 'suggestion']),
})

export const VenueNewFacilityBody = z.object({
  venue: z.string().trim().min(1).max(200),
  name: z.string().trim().min(1).max(200),
  kind: z.enum(['venue', 'outdoor', 'office', 'storage', 'other']),
})

export const RoomReviewBody = z.object({ note: z.string().trim().max(1000).nullable().optional() })

// ------------------------------------------------------------- helpers

// A name compared for a suggestion: case, accents, punctuation, "&" against
// "and" and a leading "The" don't count.
export function normalizeVenueName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/^the /, '')
}

// The venue key for a room: Booking's venue name, trimmed.
export const venueKey = (room: Pick<SourceRoom, 'venue'>) => room.venue.trim()

// The best Hub kind for a Booking room's name; "room" when nothing fits.
// Staff can change it in Hub afterward: Booking has no kinds.
export function spaceKindFor(name: string): SpaceKind {
  const n = name.toLowerCase()
  if (/\b(arena|hall|ballroom|auditorium|gym|gymnasium)\b/.test(n)) return 'hall'
  if (/\bbackstage|green room|dressing\b/.test(n)) return 'backstage'
  if (/\bstage\b/.test(n)) return 'stage'
  if (/\b(lobby|foyer|concourse)\b/.test(n)) return 'lobby'
  if (/\bkitchen\b/.test(n)) return 'kitchen'
  if (/\bconcession/.test(n)) return 'concessions'
  if (/\boffice\b/.test(n)) return 'office'
  if (/\bstorage\b/.test(n)) return 'storage'
  if (/\b(patio|lawn|plaza|deck|terrace|park|riverfront|outdoor|courtyard|grounds)\b/.test(n)) return 'outdoor'
  return 'room'
}
